import { PREVIEW_MODE, EXTERNAL_REDIRECT_TIMEOUT } from '$lib/global/constants';

import { goto } from '$app/navigation';
import { preloadCode, preloadData } from '$app/navigation';
import { base } from '$app/paths';
import { createExternalUrl } from '$lib/helper';
import { disabledNavigation } from '$lib/Stores/stores';
import { createGoToUrlWithPreservedQueries } from '$lib/utils/createGoToUrlWithPreservedQueries';

import debug from 'debug';
import { get } from 'svelte/store';

import { getResultPageId } from './get-result-page-id';

import type { TrackingProperty } from '$lib/types/TrackingProperty';

const debugLog = debug('app:next-page-navigator');

export class NextPageNavigator {
    private readonly campaignId: string;
    private readonly trackingProperties?: TrackingProperty[];
    private readonly firstResultPage: string;
    private readonly pageIdToSlug: {
        [pageId: string]: string;
    };
    private readonly pathTo404: string;

    constructor(
        campaignId: string,
        firstResultPage: string,
        pageIdToSlugMap: { [pageId: string]: string } = {},
        trackingProperties?: TrackingProperty[],
    ) {
        this.campaignId = campaignId;
        this.firstResultPage = firstResultPage;
        this.pageIdToSlug = pageIdToSlugMap;
        this.pathTo404 = `${base}/404`;
        this.trackingProperties = trackingProperties;
    }

    go(nextPage: string) {
        debugLog('-> go', nextPage);

        if (get(disabledNavigation)) {
            return;
        }

        // Next page is a result page
        if (nextPage === 'result') {
            // Get result page with the highest count
            const resultPageId = getResultPageId(this.campaignId, this.firstResultPage);

            // Navigate to result page
            if (resultPageId) {
                return this.gotoSlug(resultPageId);
            }
        }

        // Navigate to regular next page
        if (nextPage) {
            return this.gotoSlug(nextPage);
        }

        return goto(this.pathTo404);
    }

    async gotoSlug(
        pageId: string,
        options?: {
            replaceState?: boolean | undefined;
            noScroll?: boolean | undefined;
            keepFocus?: boolean | undefined;
            state?: any;
            invalidateAll?: boolean | undefined;
        },
    ) {
        if (get(disabledNavigation)) {
            return;
        }

        const slug = this.pageIdToSlug[pageId];

        debugLog('-> gotoSlug', slug);

        if (PREVIEW_MODE) {
            const gotoUrl = new URL(
                slug ? `${base}/${slug}` : `${base}/${pageId}`,
                window.location.origin,
            );

            const { url } = createGoToUrlWithPreservedQueries(gotoUrl.toString());
            return goto(url, { ...options, noScroll: true });
        }

        if (slug) {
            return goto(`${base}/${slug}`, options);
        }

        return goto(`${base}/${pageId}`, options);
    }

    getNextPageUrl(nextPage: string): string {
        // Next page is a result page
        if (nextPage === 'result') {
            const resultPageId = getResultPageId(this.campaignId, this.firstResultPage);

            if (resultPageId) {
                const slug = this.pageIdToSlug[resultPageId] || resultPageId;

                return `${base}/${slug}`;
            }
        }

        // Regular next page
        if (nextPage) {
            const slug = this.pageIdToSlug[nextPage] || nextPage;

            return `${base}/${slug}`;
        }

        return this.pathTo404;
    }

    isIframe(): boolean {
        return window.self !== window.top;
    }

    goExternal(externalLink: string) {
        if (get(disabledNavigation)) {
            return;
        }

        const url = createExternalUrl(externalLink, this.campaignId, this.trackingProperties);

        // This timeout is a workaround to ensure that button click tracking events are sent before
        // the navigation happens and the page is torn down. Unfortunately the tracking request itself
        // is not a Promise as of now.

        // This is also _not needed_ when the external link is opened in a new tab -> We don't have
        // a setting for this yet, but will potentially add it in the future.
        setTimeout(() => {
            if (this.isIframe()) {
                window.open(url);
            } else {
                return window.location.assign(url);
            }
        }, EXTERNAL_REDIRECT_TIMEOUT);
    }

    preload(nextPageId: string, isExternal: boolean) {
        if (nextPageId && !isExternal) {
            let pageSlug = this.pageIdToSlug[nextPageId];

            if (nextPageId === 'result') {
                const resultPageId = getResultPageId(this.campaignId, this.firstResultPage);

                pageSlug = resultPageId ? this.pageIdToSlug[resultPageId] : pageSlug;
            }

            void preloadData(`${base}/${pageSlug}`);
            void preloadCode(`${base}/${pageSlug}`);
        }
    }
}
